import React, { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { BASE_API_URL } from "../../../utils/consts";
import { useSelector, useDispatch } from "react-redux";
import { getUserWorkLocationsHelper } from "../../../utils/helpers/workLocationsHelper";
import { useAuth } from "../../../utils/useAuth";
import "./UserHourlyRate.scss";

type Props = {
  isOpen: boolean;
  onCloseModal: () => void;
  userId: any;
  onUpdate: () => void;
  userData: any;
};

const RATE_FIELDS = [
  {
    key: "hourlyRate",
    label: "Satnica",
    unit: "RSD",
    backendKey: "hourlyRates",
  },
  {
    key: "percentageRate",
    label: "Procenat",
    unit: "%",
    backendKey: "percentageRates",
  },
  {
    key: "dailyWage",
    label: "Dnevnica",
    unit: "RSD",
    backendKey: "dailyWages",
  },
  {
    key: "temporaryHelp",
    label: "Ispomoć",
    unit: "RSD",
    backendKey: "temporaryHelps",
  },
  {
    key: "travelExpenses",
    label: "Putni troškovi",
    unit: "RSD",
    backendKey: "travelExpenses",
  },
  {
    key: "fixedSalary",
    label: "Fiksna zarada",
    unit: "RSD",
    backendKey: "fixedSalaries",
  },
];

const emptyForm = {
  date: "",
  locationId: "",
  hourlyRate: "",
  percentageRate: "",
  dailyWage: "",
  temporaryHelp: "",
  travelExpenses: "",
  fixedSalary: "",
  description: "",
};

function salaryEntry(item: any) {
  return {
    hourlyRate: item.salaryTypes?.hourlyRates ?? 0,
    percentageRate: item.salaryTypes?.percentageRates ?? 0,
    dailyWage: item.salaryTypes?.dailyWages ?? 0,
    temporaryHelp: item.salaryTypes?.temporaryHelps ?? 0,
    travelExpenses: item.salaryTypes?.travelExpenses ?? 0,
    fixedSalary: item.salaryTypes?.fixedSalaries ?? 0,
  };
}

function summaryOf(e: ReturnType<typeof salaryEntry>): {
  v: string;
  u: string;
} {
  const fmt = (n: number) => n.toLocaleString("sr-RS");
  if (e.fixedSalary) return { v: fmt(e.fixedSalary), u: "RSD fiksno" };
  if (e.hourlyRate) return { v: fmt(e.hourlyRate), u: "RSD/h" };
  if (e.dailyWage) return { v: fmt(e.dailyWage), u: "RSD/dan" };
  return { v: "—", u: "" };
}

const UserHourlyRatePercentageModal: React.FC<Props> = ({
  isOpen,
  onCloseModal,
  userId,
  onUpdate,
  userData,
}) => {
  const dispatch = useDispatch();
  const authUser: any = useAuth();
  const workLocationsState = useSelector((state: any) => state.workLocations);

  const [userWorkLocations, setUserWorkLocations] = useState<any[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [pane, setPane] = useState<"hist" | "add">("hist");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseModal();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, onCloseModal]);

  useEffect(() => {
    if (!isOpen || !userData?._id) return;
    loadUserWorkLocations();
    setPane("hist");
    setFormData({ ...emptyForm });
    const sorted = (
      userData?.salary?.filter((s: any) => s !== null) || []
    ).sort(
      (a: any, b: any) =>
        new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
    setExpandedId(sorted[0]?._id ?? null);
  }, [isOpen, userData?._id]);

  const loadUserWorkLocations = async () => {
    try {
      setLoadingLocations(true);
      const locs = await getUserWorkLocationsHelper(
        userData._id,
        authUser.accessToken,
        dispatch,
        workLocationsState,
      );
      setUserWorkLocations(locs || []);
    } catch (err) {
      console.error("Error loading work locations:", err);
    } finally {
      setLoadingLocations(false);
    }
  };

  const setField = (key: string, value: string) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!formData.date) return;
    try {
      setSaving(true);
      await axios.put(`${BASE_API_URL}/addSalary/${userId}`, {
        salary: {
          date: formData.date,
          locationId: formData.locationId || null,
          salaryTypes: {
            hourlyRates: Number(formData.hourlyRate) || 0,
            percentageRates: Number(formData.percentageRate) || 0,
            dailyWages: Number(formData.dailyWage) || 0,
            temporaryHelps: Number(formData.temporaryHelp) || 0,
            travelExpenses: Number(formData.travelExpenses) || 0,
            fixedSalaries: Number(formData.fixedSalary) || 0,
          },
        },
      });
      onUpdate();
      setFormData({ ...emptyForm });
      setPane("hist");
    } catch (err) {
      console.error("Error adding salary:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId: string) => {
    if (!window.confirm("Obrisati ovaj unos prihoda?")) return;
    try {
      await axios.delete(
        `${BASE_API_URL}/deleteSalary/${userData._id}/${itemId}`,
      );
      onUpdate();
    } catch (err) {
      console.error(err);
    }
  };

  if (!isOpen) return null;

  const salaryItems = (
    userData?.salary?.filter((s: any) => s !== null) || []
  ).sort(
    (a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  const locName = (item: any): string | null => {
    if (!item.locationId) return null;
    if (typeof item.locationId === "object") return item.locationId.name ?? null;
    const found = userWorkLocations.find(
      (l: any) => l._id === item.locationId || l._id?.toString() === item.locationId,
    );
    return found?.name ?? null;
  };
  return (
    <div
      className="am-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCloseModal();
      }}
    >
      <div className="am-modal" style={{ maxWidth: 720 }}>
        {/* ── Header ── */}
        <header className="am-head">
          <span className="am-ic">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 17l5-5 4 3 6-7" />
              <path d="M3 21h18" />
            </svg>
          </span>
          <div className="am-head-meta">
            <div className="am-eyebrow">
              Prihodi · {userData?.username || ""}
            </div>
            <h2 className="am-title">Lista prihoda zaposlenog</h2>
            <div className="am-desc">
              Svaki unos važi od svog datuma. Najnoviji datum je aktuelni
              obračun.
            </div>
          </div>
          <button
            className="am-close"
            onClick={onCloseModal}
            aria-label="Zatvori"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>

        {/* ── Body ── */}
        <div className="am-body">
          {/* Tab switcher */}
          <div className="uhr-seg">
            <button
              className={pane === "hist" ? "active" : ""}
              onClick={() => setPane("hist")}
            >
              Istorija <span className="uhr-cnt">{salaryItems.length}</span>
            </button>
            <button
              className={pane === "add" ? "active" : ""}
              onClick={() => setPane("add")}
            >
              + Novi unos
            </button>
          </div>

          {/* ── History pane ── */}
          {pane === "hist" && (
            <div className="uhr-timeline">
              {salaryItems.length === 0 ? (
                <div className="uhr-empty">
                  <svg
                    width="34"
                    height="34"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  >
                    <path d="M3 17l5-5 4 3 6-7" />
                  </svg>
                  <div>Još nema unetih prihoda.</div>
                </div>
              ) : (
                salaryItems.map((item: any, i: number) => {
                  const isCur = i === 0;
                  const isOpen = expandedId === item._id;
                  const entry = salaryEntry(item);
                  const sum = summaryOf(entry);
                  const loc = locName(item);

                  return (
                    <div
                      key={item._id}
                      className={`uhr-inc${isOpen ? " open" : ""}${isCur ? " uhr-cur" : ""}`}
                    >
                      <div
                        className="uhr-inc-row"
                        onClick={() => setExpandedId(isOpen ? null : item._id)}
                      >
                        <span className="uhr-inc-chev">
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                          >
                            <path d="M9 6l6 6-6 6" />
                          </svg>
                        </span>
                        <span className="uhr-inc-date">
                          {format(new Date(item.date), "yyyy-MM-dd")}
                          {isCur && (
                            <span className="uhr-badge-cur">Aktuelno</span>
                          )}
                        </span>
                        {loc && (
                          <span className="uhr-inc-loc">
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M12 21s7-6.4 7-11a7 7 0 1 0-14 0c0 4.6 7 11 7 11z" />
                              <circle cx="12" cy="10" r="2.4" />
                            </svg>
                            {loc}
                          </span>
                        )}
                        <span className="uhr-inc-sum">
                          <span className="uhr-sum-v">{sum.v}</span>
                          {sum.u && <span className="uhr-sum-u">{sum.u}</span>}
                        </span>
                        <button
                          className="uhr-inc-del"
                          title="Izbriši unos"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item._id);
                          }}
                        >
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" />
                          </svg>
                        </button>
                      </div>

                      <div className="uhr-inc-detail">
                        <div className="uhr-rates">
                          {RATE_FIELDS.map((r) => {
                            const val = entry[
                              r.key as keyof typeof entry
                            ] as number;
                            const isZero = val === 0;
                            const isHl = r.key === "fixedSalary" && val > 0;
                            return (
                              <div
                                key={r.key}
                                className={`uhr-rate${isZero ? " uhr-rate-zero" : ""}${isHl ? " uhr-rate-hl" : ""}`}
                              >
                                <div className="uhr-rate-k">{r.label}</div>
                                <div className="uhr-rate-val">
                                  {val.toLocaleString("sr-RS")}
                                  <span className="uhr-rate-u">{r.unit}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ── Add pane ── */}
          {pane === "add" && (
            <div>
              <div className="uhr-modal-section">
                <div className="uhr-section-label">
                  Period i lokacija <span className="uhr-ln" />
                </div>
                <div className="am-form-grid">
                  <div className="am-field">
                    <label className="am-label" htmlFor="uhr-date">
                      Važi od <span className="am-req">*</span>
                    </label>
                    <input
                      id="uhr-date"
                      type="date"
                      className="am-input uhr-date-input"
                      value={formData.date}
                      onChange={(e) => setField("date", e.target.value)}
                    />
                  </div>
                  <div className="am-field">
                    <label className="am-label" htmlFor="uhr-loc">
                      Radna lokacija
                    </label>
                    <div className="am-select-wrap">
                      <select
                        id="uhr-loc"
                        className={`am-select${!formData.locationId ? " am-placeholder" : ""}`}
                        value={formData.locationId}
                        onChange={(e) => setField("locationId", e.target.value)}
                        disabled={loadingLocations}
                      >
                        <option value="">
                          {loadingLocations
                            ? "Učitavanje…"
                            : "Izaberite lokaciju"}
                        </option>
                        {userWorkLocations.map((loc: any) => (
                          <option key={loc._id} value={loc._id}>
                            {loc.name}
                          </option>
                        ))}
                      </select>
                      <span className="am-chev">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="uhr-modal-section">
                <div className="uhr-section-label">
                  Stope i naknade <span className="uhr-ln" />
                </div>
                <div className="am-form-grid">
                  {RATE_FIELDS.map((r) => (
                    <div key={r.key} className="am-field">
                      <label className="am-label" htmlFor={`uhr-rf-${r.key}`}>
                        {r.label}
                      </label>
                      <div className="uhr-num-affix">
                        <input
                          id={`uhr-rf-${r.key}`}
                          type="number"
                          inputMode="decimal"
                          min="0"
                          placeholder="0"
                          className="am-input"
                          value={formData[r.key as keyof typeof formData]}
                          onChange={(e) => setField(r.key, e.target.value)}
                        />
                        <span className="uhr-unit">{r.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="uhr-modal-section">
                <div className="uhr-section-label">
                  Napomena <span className="uhr-ln" />
                </div>
                <div className="am-form-grid">
                  <div className="am-field am-span-2">
                    <label className="am-label" htmlFor="uhr-desc">
                      Opis <span className="am-field-hint">opciono</span>
                    </label>
                    <textarea
                      id="uhr-desc"
                      className="am-textarea"
                      placeholder="Razlog izmene, dodatne napomene…"
                      value={formData.description}
                      onChange={(e) => setField("description", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <footer className="am-foot">
          {pane === "hist" ? (
            <>
              <span className="am-foot-note">
                {salaryItems.length}{" "}
                {salaryItems.length === 1 ? "unos" : "unosa"} u istoriji
              </span>
              <div className="am-foot-spacer" />
              <button className="am-btn" onClick={onCloseModal}>
                Zatvori
              </button>
              <button
                className="am-btn am-btn-primary"
                onClick={() => setPane("add")}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Novi unos
              </button>
            </>
          ) : (
            <>
              <span className="am-foot-note">
                <span className="am-req">*</span> Datum i lokacija su obavezni
              </span>
              <div className="am-foot-spacer" />
              <button className="am-btn" onClick={() => setPane("hist")}>
                Nazad na istoriju
              </button>
              <button
                className="am-btn am-btn-primary"
                onClick={handleSave}
                disabled={!formData.date || saving}
              >
                {saving ? "Čuvanje…" : "Sačuvaj prihod"}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
};

export default UserHourlyRatePercentageModal;
